import {
  getAtomBalance,
  getBalanceHint,
  isEquationBalanced,
  isSimplestCoefficientRatio,
  parseEquation,
  type AtomBalance,
} from './chemicalEquation';

export type CombustionMode = 'rusting' | 'fire-and-rust' | 'equations';

/** Stable identity used by reviewed CurriculumToolBinding records. */
export const COMBUSTION_CORROSION_TOOL_IDENTITY = {
  id: 'combustion-corrosion',
  toolKey: 'chemistry-combustion-corrosion',
  toolVersion: '1.0.0',
  lifecycle: 'live',
  computation: 'client-deterministic',
  evidencePolicy: 'none',
  masteryPolicy: 'not-assessed',
  curriculumPolicy: 'reviewed-binding-required',
} as const;

/** CT-MODE: modes in ascending start Year (study-chemistry-tools-prd.md §8.2). */
export const COMBUSTION_MODES: readonly { mode: CombustionMode; fromYear: number }[] = [
  { mode: 'rusting', fromYear: 6 },
  { mode: 'fire-and-rust', fromYear: 8 },
  { mode: 'equations', fromYear: 9 },
];

export interface CombustionDataProvenance {
  sources: string[];
  conditions: string;
  reviewStatus: 'pending-teaching-review';
}

export const COMBUSTION_DATA_PROVENANCE: CombustionDataProvenance = {
  sources: [
    'Standard secondary science textbook rusting investigation (iron nails in test tubes, about one week)',
    'Standard secondary chemistry descriptions of the fire triangle and complete and incomplete combustion',
    'IUPAC formulae and conventional balanced equations for combustion, respiration and photosynthesis',
    'Fire and Rescue NSW and Queensland Fire Department public fire-safety guidance (concept level only)',
  ],
  conditions:
    'Classroom conditions, about 20–25 °C and 100 kPa. Rust levels are qualitative model outcomes after about one week, not measurements.',
  reviewStatus: 'pending-teaching-review',
};

export const MODEL_BOUNDARIES: string[] = [
  'Rust levels are qualitative: none, a little or a lot after about one week. Real rates also depend on temperature, the steel and the surface.',
  'Rust is hydrated iron(III) oxide, Fe₂O₃·xH₂O, where x varies. The Year 9 equation balances the iron(III) oxide part only and leaves the water of hydration out.',
  'The fire triangle is a teaching model. It is not fire-safety training. In a real fire, get out, stay out and call Triple Zero (000).',
  'Burning, heating and gas experiments need a teacher, a risk assessment and safety equipment. Do not try them at home.',
];

// ---------------------------------------------------------------------------------------------
// Rusting model (Year 6 and Year 8)
// ---------------------------------------------------------------------------------------------

export type RustLevel = 'none' | 'some' | 'lots';

export type IronProtection =
  | 'bare'
  | 'oil'
  | 'paint'
  | 'galvanised'
  | 'galvanised-scratched'
  | 'stainless'
  | 'magnesium-strip';

export type TubeLiquid = 'none' | 'tap-water' | 'boiled-water' | 'salt-water' | 'boiled-salt-water';

export interface RustConditions {
  water: boolean;
  oxygen: boolean;
  salt: boolean;
  protection: IronProtection;
}

/** What the code-drawn test tube shows. It must agree with the conditions (tested). */
export interface TubeSetup {
  liquid: TubeLiquid;
  oilLayer: boolean;
  dryingAgent: boolean;
  stoppered: boolean;
}

export interface RustCase {
  id: string;
  title: string;
  context: string;
  conditions: RustConditions;
  tube: TubeSetup;
  /** Why the model result happens. Shown for every answer. */
  explanation: string;
  /** Specific correction shown when the learner predicted a different level. */
  misconception: string;
}

export interface RustModelResult {
  level: RustLevel;
  reason: string;
}

export const RUST_LEVEL_LABELS: Record<RustLevel, string> = {
  none: 'No rust',
  some: 'A little rust',
  lots: 'Lots of rust',
};

export const RUST_LEVEL_ORDER: RustLevel[] = ['none', 'some', 'lots'];

export const PROTECTION_LABELS: Record<IronProtection, string> = {
  bare: 'Bare iron nail',
  oil: 'Nail coated in oil',
  paint: 'Painted nail',
  galvanised: 'Galvanised nail (zinc coating)',
  'galvanised-scratched': 'Galvanised nail with a scratch',
  stainless: 'Stainless steel nail',
  'magnesium-strip': 'Bare nail wrapped in magnesium ribbon',
};

const KNOWN_PROTECTIONS = new Set<IronProtection>(Object.keys(PROTECTION_LABELS) as IronProtection[]);

/**
 * Deterministic rusting model. Iron rusts only when water and oxygen both reach the iron surface.
 * Salt (an electrolyte) speeds rusting. Barriers keep water and oxygen away; zinc and magnesium
 * corrode instead of iron (sacrificial protection); stainless steel has a protective chromium
 * oxide layer. Unknown protection fails closed (returns null).
 */
export function rustModel(conditions: RustConditions): RustModelResult | null {
  if (!KNOWN_PROTECTIONS.has(conditions.protection)) return null;
  if (!conditions.water) {
    return { level: 'none', reason: 'No water reaches the iron, so it cannot rust.' };
  }
  if (!conditions.oxygen) {
    return { level: 'none', reason: 'No oxygen reaches the iron, so it cannot rust.' };
  }
  switch (conditions.protection) {
    case 'oil':
    case 'paint':
      return {
        level: 'none',
        reason: 'The coating is a barrier that keeps water and oxygen off the iron.',
      };
    case 'galvanised':
    case 'galvanised-scratched':
      return {
        level: 'none',
        reason:
          'Zinc is more reactive than iron. It corrodes first, so it protects the iron even where the coating is scratched (sacrificial protection).',
      };
    case 'magnesium-strip':
      return {
        level: 'none',
        reason:
          'Magnesium is more reactive than iron. It corrodes instead of the iron while they touch (sacrificial protection).',
      };
    case 'stainless':
      return {
        level: 'none',
        reason:
          'Chromium in stainless steel forms a thin, tough oxide layer that seals the surface.',
      };
    case 'bare':
      return conditions.salt
        ? {
            level: 'lots',
            reason:
              'Water and oxygen are both present, and dissolved salt helps the reaction go faster.',
          }
        : { level: 'some', reason: 'Water and oxygen are both present, so the iron rusts.' };
  }
}

export function checkRustPrediction(
  entry: RustCase,
  prediction: RustLevel,
): { correct: boolean; expected: RustLevel; reason: string; feedback: string } | null {
  const model = rustModel(entry.conditions);
  if (!model) return null;
  const correct = model.level === prediction;
  return {
    correct,
    expected: model.level,
    reason: model.reason,
    feedback: correct ? entry.explanation : entry.misconception,
  };
}

const BARE_OPEN: Omit<TubeSetup, 'liquid'> = { oilLayer: false, dryingAgent: false, stoppered: false };

export const YEAR_6_RUST_CASES: RustCase[] = [
  {
    id: 'y6-dry-air',
    title: 'Dry air with a drying agent',
    context:
      'A nail sits in a stoppered tube with calcium chloride granules. The granules soak up water vapour, so the air stays dry.',
    conditions: { water: false, oxygen: true, salt: false, protection: 'bare' },
    tube: { liquid: 'none', oilLayer: false, dryingAgent: true, stoppered: true },
    explanation:
      'There is oxygen in the air, but the drying agent removes the water. Without water the nail stays shiny.',
    misconception:
      'Air alone is not enough. The drying agent took the water out of the air, and iron needs water as well as oxygen to rust.',
  },
  {
    id: 'y6-boiled-water-oil',
    title: 'Boiled water under a layer of oil',
    context:
      'The water was boiled to drive out dissolved air, cooled, then covered with a layer of oil so air cannot dissolve back in.',
    conditions: { water: true, oxygen: false, salt: false, protection: 'bare' },
    tube: { liquid: 'boiled-water', oilLayer: true, dryingAgent: false, stoppered: true },
    explanation:
      'The nail is under water, but boiling removed the oxygen and the oil stops new air getting in. No oxygen means no rust.',
    misconception:
      'Water alone is not enough. Boiling removed the dissolved oxygen and the oil layer keeps air out, so the iron has no oxygen to react with.',
  },
  {
    id: 'y6-tap-water-air',
    title: 'Tap water and air',
    context: 'The nail is half in ordinary tap water. The tube is open to the air.',
    conditions: { water: true, oxygen: true, salt: false, protection: 'bare' },
    tube: { liquid: 'tap-water', ...BARE_OPEN },
    explanation:
      'The nail has both water and oxygen, so orange-brown rust forms, especially near the water surface.',
    misconception:
      'This tube has both things iron needs to rust: water, and oxygen from the air and dissolved in the water. Rust appears within a few days.',
  },
  {
    id: 'y6-salt-water-air',
    title: 'Salt water and air',
    context: 'The nail is half in salty water. The tube is open to the air.',
    conditions: { water: true, oxygen: true, salt: true, protection: 'bare' },
    tube: { liquid: 'salt-water', ...BARE_OPEN },
    explanation:
      'Water and oxygen are both here, and the salt speeds up rusting. This nail rusts the most.',
    misconception:
      'Salt does not stop rusting — it speeds it up. That is why cars and jetties near the sea rust faster.',
  },
  {
    id: 'y6-painted-tap-water',
    title: 'Painted nail in tap water and air',
    context: 'A nail is fully painted, then placed half in tap water. The tube is open to the air.',
    conditions: { water: true, oxygen: true, salt: false, protection: 'paint' },
    tube: { liquid: 'tap-water', ...BARE_OPEN },
    explanation:
      'Water and oxygen are in the tube, but the paint stops them touching the iron, so the nail does not rust.',
    misconception:
      'The tube has water and air, but they cannot reach the iron through unbroken paint. That is why we paint gates and bridges.',
  },
  {
    id: 'y6-oiled-tap-water',
    title: 'Oiled nail in tap water and air',
    context: 'A nail is wiped all over with oil, then placed half in tap water. The tube is open to the air.',
    conditions: { water: true, oxygen: true, salt: false, protection: 'oil' },
    tube: { liquid: 'tap-water', ...BARE_OPEN },
    explanation:
      'Water and oxygen are in the tube, but the oil coating keeps them off the iron, so the nail does not rust.',
    misconception:
      'A thin layer of oil or grease is a barrier, like paint. That is why bike chains and tools are oiled.',
  },
  {
    id: 'y6-boiled-salt-water-oil',
    title: 'Boiled salt water under a layer of oil',
    context:
      'Salty water was boiled to drive out dissolved air, cooled, then covered with oil. The tube is stoppered.',
    conditions: { water: true, oxygen: false, salt: true, protection: 'bare' },
    tube: { liquid: 'boiled-salt-water', oilLayer: true, dryingAgent: false, stoppered: true },
    explanation:
      'Salt speeds up rusting only when rusting can happen. Boiling removed the oxygen and the oil keeps air out, so the nail stays shiny.',
    misconception:
      'Salt cannot make iron rust by itself. Without oxygen there is no rust, even in salty water.',
  },
  {
    id: 'y6-galvanised-tap-water',
    title: 'Zinc-coated nail in tap water and air',
    context:
      'A galvanised nail (iron coated with zinc) is placed half in tap water. The tube is open to the air.',
    conditions: { water: true, oxygen: true, salt: false, protection: 'galvanised' },
    tube: { liquid: 'tap-water', ...BARE_OPEN },
    explanation:
      'The zinc coating protects the iron. Zinc reacts first and keeps the iron from rusting.',
    misconception:
      'Water and air are here, but the zinc coating protects the iron underneath. Galvanised roofs and fences last for years.',
  },
];

export const YEAR_8_RUST_CASES: RustCase[] = [
  {
    id: 'y8-bare-tap',
    title: 'Bare nail · tap water + air',
    context: 'An uncoated iron nail half in tap water, open to the air.',
    conditions: { water: true, oxygen: true, salt: false, protection: 'bare' },
    tube: { liquid: 'tap-water', ...BARE_OPEN },
    explanation: 'Water and oxygen both reach bare iron, so it rusts at a normal rate.',
    misconception:
      'Nothing protects this iron and it has both water and oxygen, so some rust forms within a week.',
  },
  {
    id: 'y8-bare-salt',
    title: 'Bare nail · salt water + air',
    context: 'An uncoated iron nail half in salt water, open to the air.',
    conditions: { water: true, oxygen: true, salt: true, protection: 'bare' },
    tube: { liquid: 'salt-water', ...BARE_OPEN },
    explanation:
      'Dissolved salt makes the water a better conductor of ions, which speeds up the rusting reaction.',
    misconception:
      'Salt water accelerates rusting rather than slowing it. Compare it with the tap-water nail: this one rusts faster.',
  },
  {
    id: 'y8-bare-dry',
    title: 'Bare nail · dry air',
    context: 'An uncoated nail in a stoppered tube with a drying agent.',
    conditions: { water: false, oxygen: true, salt: false, protection: 'bare' },
    tube: { liquid: 'none', oilLayer: false, dryingAgent: true, stoppered: true },
    explanation: 'Oxygen is present but water is missing, so no rust forms.',
    misconception: 'Rusting needs water and oxygen together. The drying agent removed the water.',
  },
  {
    id: 'y8-boiled-salt-oil',
    title: 'Bare nail · boiled salt water under oil',
    context:
      'Salt water was boiled to remove dissolved air, cooled and sealed under oil. Salt is present but oxygen is not.',
    conditions: { water: true, oxygen: false, salt: true, protection: 'bare' },
    tube: { liquid: 'boiled-salt-water', oilLayer: true, dryingAgent: false, stoppered: true },
    explanation:
      'Salt only speeds rusting when water and oxygen are already present. With no oxygen, there is no rust.',
    misconception:
      'Salt is an accelerator, not an ingredient. Without oxygen the reaction cannot happen at all, however salty the water.',
  },
  {
    id: 'y8-oiled-tap',
    title: 'Oiled nail · tap water + air',
    context: 'The nail was wiped with oil before going half into tap water.',
    conditions: { water: true, oxygen: true, salt: false, protection: 'oil' },
    tube: { liquid: 'tap-water', ...BARE_OPEN },
    explanation:
      'The oil film is a barrier. Water and oxygen cannot reach the iron, so it does not rust while the oil stays on.',
    misconception:
      'Water and air are in the tube, but the oil coating keeps them off the iron. Bike chains and tools are oiled for this reason.',
  },
  {
    id: 'y8-painted-salt',
    title: 'Painted nail · salt water + air',
    context: 'A fully painted nail half in salt water, open to the air.',
    conditions: { water: true, oxygen: true, salt: true, protection: 'paint' },
    tube: { liquid: 'salt-water', ...BARE_OPEN },
    explanation: 'Unbroken paint keeps water, oxygen and salt away from the iron.',
    misconception:
      'Salt only speeds up rusting when it reaches the iron. Intact paint keeps the salt water off the metal.',
  },
  {
    id: 'y8-galvanised-salt',
    title: 'Galvanised nail · salt water + air',
    context: 'An iron nail coated with zinc (galvanised), half in salt water.',
    conditions: { water: true, oxygen: true, salt: true, protection: 'galvanised' },
    tube: { liquid: 'salt-water', ...BARE_OPEN },
    explanation:
      'The zinc coating protects the iron. You may see some white zinc corrosion, but no orange-brown rust.',
    misconception:
      'Galvanising protects iron in two ways: the zinc is a barrier, and zinc corrodes in preference to iron. White zinc corrosion is not rust.',
  },
  {
    id: 'y8-galvanised-scratched',
    title: 'Scratched galvanised nail · tap water + air',
    context: 'A galvanised nail has a deep scratch that shows the iron underneath.',
    conditions: { water: true, oxygen: true, salt: false, protection: 'galvanised-scratched' },
    tube: { liquid: 'tap-water', ...BARE_OPEN },
    explanation:
      'Zinc is more reactive than iron, so it keeps corroding instead of the exposed iron. This is sacrificial protection.',
    misconception:
      'Unlike paint, a scratched zinc coating still protects. The more reactive zinc gives up electrons instead of the iron.',
  },
  {
    id: 'y8-stainless-tap',
    title: 'Stainless steel nail · tap water + air',
    context: 'A nail made of stainless steel (iron alloyed with chromium) half in tap water.',
    conditions: { water: true, oxygen: true, salt: false, protection: 'stainless' },
    tube: { liquid: 'tap-water', ...BARE_OPEN },
    explanation:
      'Chromium forms a thin oxide layer on the surface that stops water and oxygen reaching the iron.',
    misconception:
      'Stainless steel is still mostly iron, but its chromium oxide layer seals the surface. It resists rust in tap water for far longer than this test.',
  },
  {
    id: 'y8-magnesium-salt',
    title: 'Nail wrapped in magnesium · salt water + air',
    context: 'A bare nail is wrapped with magnesium ribbon, then placed in salt water.',
    conditions: { water: true, oxygen: true, salt: true, protection: 'magnesium-strip' },
    tube: { liquid: 'salt-water', ...BARE_OPEN },
    explanation:
      'Magnesium is more reactive than iron, so the magnesium corrodes instead. Ships and pipelines use blocks of a reactive metal in the same way.',
    misconception:
      'Even in salt water, the iron is protected while it touches the magnesium. The magnesium is sacrificed instead of the iron.',
  },
];

/**
 * What the drawn tube implies about water, oxygen and salt. A dry tube still holds air; boiled
 * water under oil has no dissolved oxygen. Used to check tube drawings against case conditions.
 */
export function tubeImpliesConditions(tube: TubeSetup): Pick<RustConditions, 'water' | 'oxygen' | 'salt'> {
  const water = tube.liquid !== 'none';
  const deoxygenated =
    (tube.liquid === 'boiled-water' || tube.liquid === 'boiled-salt-water') && tube.oilLayer;
  const oxygen = water ? !deoxygenated : true;
  const salt = tube.liquid === 'salt-water' || tube.liquid === 'boiled-salt-water';
  return { water, oxygen, salt };
}

// ---------------------------------------------------------------------------------------------
// Fire triangle (Year 8)
// ---------------------------------------------------------------------------------------------

export type FireAnswer = 'fuel' | 'heat' | 'oxygen' | 'unsafe';

export interface FireScenario {
  id: string;
  title: string;
  context: string;
  answer: FireAnswer;
  explanation: string;
  misconception: string;
  /** Safer action, shown for unsafe scenarios. */
  saferAction?: string;
}

export const FIRE_ANSWER_LABELS: Record<FireAnswer, string> = {
  fuel: 'Removes fuel',
  heat: 'Removes heat',
  oxygen: 'Removes oxygen',
  unsafe: 'Unsafe — makes it worse',
};

export const FIRE_ANSWER_ORDER: FireAnswer[] = ['fuel', 'heat', 'oxygen', 'unsafe'];

export const FIRE_SCENARIOS: FireScenario[] = [
  {
    id: 'fire-blanket',
    title: 'Fire blanket over a pan',
    context: 'A small fire starts in a frying pan. A fire blanket is laid over the pan.',
    answer: 'oxygen',
    explanation: 'The blanket smothers the fire and cuts off the oxygen supply from the air.',
    misconception:
      'The blanket does not cool the pan much or take away the oil. It works by stopping air (oxygen) reaching the flames.',
  },
  {
    id: 'firebreak',
    title: 'Bushfire firebreak',
    context: 'Firefighters clear a strip of dry grass and shrubs ahead of a bushfire.',
    answer: 'fuel',
    explanation: 'With nothing left to burn in the cleared strip, the fire runs out of fuel there.',
    misconception:
      'Air and heat are still present at a firebreak. The fire stops because the plants it would burn have been removed.',
  },
  {
    id: 'water-campfire',
    title: 'Water on a wood campfire',
    context: 'A bucket of water is poured over burning wood and coals.',
    answer: 'heat',
    explanation:
      'Water absorbs a lot of energy as it heats and turns to steam, cooling the wood below its ignition temperature.',
    misconception:
      'The wood is still there and air is still around it. Water works on wood fires mainly by cooling the fuel below the temperature it needs to keep burning.',
  },
  {
    id: 'bunsen-gas-off',
    title: 'Turn off the Bunsen gas tap',
    context: 'A Bunsen burner flame goes out when the gas tap is turned off.',
    answer: 'fuel',
    explanation: 'Natural gas (mostly methane) is the fuel. No gas flowing means no fuel.',
    misconception:
      'The air hole is unchanged, so oxygen is still available. The flame dies because the methane fuel stopped flowing.',
  },
  {
    id: 'candle-snuffer',
    title: 'Candle snuffer',
    context: 'A metal cup is placed over a burning candle.',
    answer: 'oxygen',
    explanation:
      'The cup traps a small amount of air. The flame uses up the oxygen and cannot get more, so it goes out.',
    misconception:
      'The wax fuel is still there. The snuffer works by cutting off fresh air, so the flame runs out of oxygen.',
  },
  {
    id: 'stop-drop-roll',
    title: 'Stop, drop and roll',
    context: 'A person’s clothing catches fire. They stop, drop to the ground and roll.',
    answer: 'oxygen',
    explanation:
      'Rolling presses the flames against the ground, smothering them and cutting off oxygen. Running would feed the fire more air.',
    misconception:
      'Rolling smothers the flames by cutting off air. Running away makes air rush past and feeds the fire more oxygen.',
  },
  {
    id: 'water-oil-fire',
    title: 'Water on burning cooking oil',
    context: 'Someone wants to throw a cup of water onto a burning pan of oil.',
    answer: 'unsafe',
    explanation:
      'Water sinks under the hot oil and instantly boils. The steam blasts burning oil out of the pan as a fireball.',
    misconception:
      'Water usually cools a fire, but not this one. It sinks below the oil, flashes to steam and throws burning oil into the room.',
    saferAction:
      'If it is safe, turn off the heat and cover the pan with a lid or fire blanket. Never move the pan. If the fire grows, get out and call Triple Zero (000).',
  },
  {
    id: 'water-electrical-fire',
    title: 'Water on a burning power board',
    context: 'A power board that is still plugged in catches fire.',
    answer: 'unsafe',
    explanation:
      'Tap water conducts electricity. Spraying it on live equipment can give the person an electric shock.',
    misconception:
      'Water could cool the fire, but the danger is electrocution. Live electrical fires need the power off and a non-conducting extinguisher.',
    saferAction:
      'If it is safe, switch the power off at the wall or the switchboard. Use a CO₂ or dry chemical powder extinguisher only if trained. Otherwise get out and call Triple Zero (000).',
  },
  {
    id: 'co2-extinguisher',
    title: 'CO₂ extinguisher on a computer fire',
    context: 'A trained staff member uses a carbon dioxide extinguisher on a smouldering computer after the power is off.',
    answer: 'oxygen',
    explanation:
      'Carbon dioxide is denser than air. It pushes air away from the fire, so the flames run out of oxygen. It leaves no water behind.',
    misconception:
      'The gas from the extinguisher is very cold, but its main job is to displace the oxygen around the fire.',
  },
];

export function checkFireAnswer(scenario: FireScenario, answer: FireAnswer) {
  const correct = scenario.answer === answer;
  return {
    correct,
    expected: scenario.answer,
    feedback: correct ? scenario.explanation : scenario.misconception,
  };
}

// ---------------------------------------------------------------------------------------------
// Complete and incomplete combustion (Year 8)
// ---------------------------------------------------------------------------------------------

export type CombustionKind = 'complete' | 'incomplete';
export type AirSupply = 'plenty' | 'limited';
export type FlameColour = 'blue' | 'yellow' | 'not-visible';

export interface CombustionScenario {
  id: string;
  title: string;
  context: string;
  fuel: string;
  airSupply: AirSupply;
  flame: FlameColour;
  /** Products in the model, as formulae. */
  products: string[];
  evidence: string[];
  explanation: string;
  misconception: string;
}

export const COMBUSTION_KIND_LABELS: Record<CombustionKind, string> = {
  complete: 'Complete combustion',
  incomplete: 'Incomplete combustion',
};

export const FLAME_LABELS: Record<FlameColour, string> = {
  blue: 'Blue, quiet flame',
  yellow: 'Yellow, sooty flame',
  'not-visible': 'No flame visible',
};

/** Plenty of oxygen → complete (CO₂ + H₂O); limited oxygen → incomplete (CO and/or C soot). */
export function combustionKind(airSupply: AirSupply): CombustionKind {
  return airSupply === 'plenty' ? 'complete' : 'incomplete';
}

export const COMBUSTION_SCENARIOS: CombustionScenario[] = [
  {
    id: 'bunsen-open',
    title: 'Bunsen burner, air hole open',
    context: 'The collar is turned so air mixes with the methane before it burns.',
    fuel: 'methane',
    airSupply: 'plenty',
    flame: 'blue',
    products: ['CO2', 'H2O'],
    evidence: ['Blue flame, roaring sound', 'No soot on a cold evaporating dish'],
    explanation:
      'With plenty of oxygen, all the carbon becomes carbon dioxide and all the hydrogen becomes water.',
    misconception:
      'A blue flame is the clue: plenty of oxygen is mixed in, so the methane burns completely to CO₂ and H₂O with no soot.',
  },
  {
    id: 'bunsen-closed',
    title: 'Bunsen burner, air hole closed',
    context: 'The collar is closed. The methane only meets air at the edge of the flame.',
    fuel: 'methane',
    airSupply: 'limited',
    flame: 'yellow',
    products: ['CO', 'C', 'CO2', 'H2O'],
    evidence: ['Yellow, wavy safety flame', 'Black soot (carbon) collects on a cold dish'],
    explanation:
      'There is not enough oxygen, so some carbon forms carbon monoxide and solid carbon (soot). Glowing soot makes the flame yellow.',
    misconception:
      'The yellow colour comes from glowing soot particles. Soot only forms when oxygen is limited, so this is incomplete combustion.',
  },
  {
    id: 'candle',
    title: 'Candle flame',
    context: 'A cold glass is held briefly in a candle flame.',
    fuel: 'paraffin wax',
    airSupply: 'limited',
    flame: 'yellow',
    products: ['C', 'CO', 'CO2', 'H2O'],
    evidence: ['Yellow flame', 'Black soot on the glass', 'Water droplets condense on the glass'],
    explanation:
      'Air cannot mix into the wax vapour quickly enough, so some carbon is left as soot. Water still forms from the hydrogen.',
    misconception:
      'Water droplets show hydrogen burned, but the black soot shows some carbon did not. That is incomplete combustion.',
  },
  {
    id: 'gas-stove',
    title: 'Well-adjusted gas stove',
    context: 'A kitchen gas burner is cleaned and adjusted.',
    fuel: 'natural gas (methane)',
    airSupply: 'plenty',
    flame: 'blue',
    products: ['CO2', 'H2O'],
    evidence: ['Even blue flame', 'Saucepan bases stay clean'],
    explanation: 'Air mixes with the gas before it burns, so combustion is complete.',
    misconception:
      'A clean blue flame and no soot mean plenty of oxygen. The products are carbon dioxide and water.',
  },
  {
    id: 'blocked-heater',
    title: 'Gas heater with a blocked flue',
    context: 'An indoor gas heater burns in a closed room and its flue is blocked.',
    fuel: 'natural gas (methane)',
    airSupply: 'limited',
    flame: 'yellow',
    products: ['CO', 'CO2', 'H2O'],
    evidence: ['Flame turns yellow or orange', 'Carbon monoxide alarm sounds'],
    explanation:
      'Oxygen is used up and not replaced, so carbon monoxide forms. CO is colourless, odourless and poisonous.',
    misconception:
      'A heater that looks normal can still make carbon monoxide when air is limited. That is why gas heaters need servicing and ventilation.',
  },
  {
    id: 'car-garage',
    title: 'Car engine in a closed garage',
    context: 'A petrol engine runs inside a closed garage.',
    fuel: 'petrol (octane model)',
    airSupply: 'limited',
    flame: 'not-visible',
    products: ['CO', 'CO2', 'H2O'],
    evidence: ['No flame to see', 'Exhaust gas test shows carbon monoxide'],
    explanation:
      'Inside the engine, some fuel does not get enough oxygen, so the exhaust contains carbon monoxide. In a closed space it builds up to dangerous levels.',
    misconception:
      'You cannot see the flame, but measured carbon monoxide is evidence of incomplete combustion. Never run an engine in a closed space.',
  },
  {
    id: 'spirit-burner',
    title: 'Ethanol spirit burner',
    context: 'A spirit burner burns ethanol in an open, well-ventilated room. A cold dish is held above it.',
    fuel: 'ethanol',
    airSupply: 'plenty',
    flame: 'blue',
    products: ['CO2', 'H2O'],
    evidence: ['Pale blue, almost invisible flame', 'Water droplets but no soot on the cold dish'],
    explanation:
      'Ethanol mixes well with air as it burns, so all its carbon becomes carbon dioxide and its hydrogen becomes water.',
    misconception:
      'A pale blue flame with no soot means plenty of oxygen. Water droplets alone do not show incomplete combustion; soot or CO would.',
  },
  {
    id: 'kerosene-lamp',
    title: 'Kerosene lamp with the wick too high',
    context: 'The wick of a kerosene lamp is turned up too far, so more fuel vaporises than the air can burn.',
    fuel: 'kerosene',
    airSupply: 'limited',
    flame: 'yellow',
    products: ['C', 'CO', 'CO2', 'H2O'],
    evidence: ['Tall, smoky yellow flame', 'Black soot on the glass chimney'],
    explanation:
      'There is more fuel vapour than oxygen can reach, so some carbon is left as soot and some forms carbon monoxide.',
    misconception:
      'The black soot is unburnt carbon. It forms only when oxygen is limited, so this is incomplete combustion. Turning the wick down fixes it.',
  },
];

export function checkCombustionAnswer(scenario: CombustionScenario, answer: CombustionKind) {
  const expected = combustionKind(scenario.airSupply);
  const correct = expected === answer;
  return {
    correct,
    expected,
    feedback: correct ? scenario.explanation : scenario.misconception,
  };
}

// ---------------------------------------------------------------------------------------------
// Year 9 equations
// ---------------------------------------------------------------------------------------------

export type EquationProcess =
  | 'combustion'
  | 'incomplete-combustion'
  | 'corrosion'
  | 'respiration'
  | 'photosynthesis';

export interface EquationSpecies {
  formula: string;
  name: string;
  state: 's' | 'l' | 'g' | 'aq';
}

export interface ProcessEquationCase {
  id: string;
  title: string;
  process: EquationProcess;
  wordEquation: string;
  reactants: EquationSpecies[];
  products: EquationSpecies[];
  /** Simplest whole-number coefficients, reactants first then products. */
  balanced: number[];
  /** Text written above the arrow, for example energy or conditions. */
  arrowNote?: string;
  /** A model simplification the learner must see before balancing. */
  boundary?: string;
  note: string;
}

export const PROCESS_LABELS: Record<EquationProcess, string> = {
  combustion: 'Complete combustion',
  'incomplete-combustion': 'Incomplete combustion',
  corrosion: 'Corrosion',
  respiration: 'Cellular respiration',
  photosynthesis: 'Photosynthesis',
};

export const MIN_COEFFICIENT = 1;
export const MAX_COEFFICIENT = 12;

const OXYGEN: EquationSpecies = { formula: 'O2', name: 'oxygen', state: 'g' };
const CARBON_DIOXIDE: EquationSpecies = { formula: 'CO2', name: 'carbon dioxide', state: 'g' };
const WATER_GAS: EquationSpecies = { formula: 'H2O', name: 'water', state: 'g' };
const WATER_LIQUID: EquationSpecies = { formula: 'H2O', name: 'water', state: 'l' };
const GLUCOSE: EquationSpecies = { formula: 'C6H12O6', name: 'glucose', state: 'aq' };

export const RUST_HYDRATE_NOTE =
  'Rust is hydrated iron(III) oxide: 4Fe(s) + 3O₂(g) + 2xH₂O(l) → 2Fe₂O₃·xH₂O(s). The amount of water, x, varies, and the same 2x water molecules appear on both sides, so this tool balances the simplified iron(III) oxide equation.';

export const PROCESS_EQUATIONS: ProcessEquationCase[] = [
  {
    id: 'methane',
    title: 'Methane burns completely',
    process: 'combustion',
    wordEquation: 'methane + oxygen → carbon dioxide + water',
    reactants: [{ formula: 'CH4', name: 'methane', state: 'g' }, OXYGEN],
    products: [CARBON_DIOXIDE, WATER_GAS],
    balanced: [1, 2, 1, 2],
    note: 'Natural gas in Bunsen burners and gas stoves is mostly methane.',
  },
  {
    id: 'propane',
    title: 'Propane burns completely',
    process: 'combustion',
    wordEquation: 'propane + oxygen → carbon dioxide + water',
    reactants: [{ formula: 'C3H8', name: 'propane', state: 'g' }, OXYGEN],
    products: [CARBON_DIOXIDE, WATER_GAS],
    balanced: [1, 5, 3, 4],
    note: 'Barbecue gas (LPG) is mostly propane.',
  },
  {
    id: 'ethanol',
    title: 'Ethanol burns completely',
    process: 'combustion',
    wordEquation: 'ethanol + oxygen → carbon dioxide + water',
    reactants: [{ formula: 'C2H5OH', name: 'ethanol', state: 'l' }, OXYGEN],
    products: [CARBON_DIOXIDE, WATER_GAS],
    balanced: [1, 3, 2, 3],
    note: 'Count the oxygen atom inside ethanol as well as those in O₂.',
  },
  {
    id: 'magnesium',
    title: 'Magnesium burns in air',
    process: 'combustion',
    wordEquation: 'magnesium + oxygen → magnesium oxide',
    reactants: [{ formula: 'Mg', name: 'magnesium', state: 's' }, OXYGEN],
    products: [{ formula: 'MgO', name: 'magnesium oxide', state: 's' }],
    balanced: [2, 1, 2],
    note: 'A bright white flame; the white ash is magnesium oxide. Do not look directly at the flame.',
  },
  {
    id: 'carbon',
    title: 'Carbon burns completely',
    process: 'combustion',
    wordEquation: 'carbon + oxygen → carbon dioxide',
    reactants: [{ formula: 'C', name: 'carbon', state: 's' }, OXYGEN],
    products: [CARBON_DIOXIDE],
    balanced: [1, 1, 1],
    note: 'Charcoal is mostly carbon. With plenty of oxygen it forms only carbon dioxide.',
  },
  {
    id: 'methane-incomplete',
    title: 'Methane burns with limited oxygen',
    process: 'incomplete-combustion',
    wordEquation: 'methane + oxygen → carbon monoxide + water',
    reactants: [{ formula: 'CH4', name: 'methane', state: 'g' }, OXYGEN],
    products: [{ formula: 'CO', name: 'carbon monoxide', state: 'g' }, WATER_GAS],
    balanced: [2, 3, 2, 4],
    note: 'Less oxygen per methane than complete combustion (3 : 2 instead of 4 : 2).',
  },
  {
    id: 'iron-rusting',
    title: 'Iron rusts (simplified)',
    process: 'corrosion',
    wordEquation: 'iron + oxygen (+ water) → hydrated iron(III) oxide (rust)',
    reactants: [{ formula: 'Fe', name: 'iron', state: 's' }, OXYGEN],
    products: [{ formula: 'Fe2O3', name: 'iron(III) oxide', state: 's' }],
    balanced: [4, 3, 2],
    boundary: RUST_HYDRATE_NOTE,
    note: 'Iron only rusts when oxygen and water are both present.',
  },
  {
    id: 'respiration',
    title: 'Cellular respiration',
    process: 'respiration',
    wordEquation: 'glucose + oxygen → carbon dioxide + water (+ energy)',
    reactants: [GLUCOSE, OXYGEN],
    products: [CARBON_DIOXIDE, WATER_LIQUID],
    balanced: [1, 6, 6, 6],
    note: 'Respiration has the same overall equation as burning glucose, but happens in controlled steps inside cells.',
  },
  {
    id: 'photosynthesis',
    title: 'Photosynthesis',
    process: 'photosynthesis',
    wordEquation: 'carbon dioxide + water → glucose + oxygen',
    reactants: [CARBON_DIOXIDE, WATER_LIQUID],
    products: [GLUCOSE, OXYGEN],
    balanced: [6, 6, 1, 6],
    arrowNote: 'light energy, chlorophyll',
    note: 'Photosynthesis is the reverse of respiration overall. Light supplies the energy.',
  },
];

export function speciesCount(entry: ProcessEquationCase): number {
  return entry.reactants.length + entry.products.length;
}

/** Builds parser input (for example `CH4 + 2O2 -> CO2 + 2H2O`). Returns null for invalid coefficients. */
export function buildEquationInput(
  entry: ProcessEquationCase,
  coefficients: readonly number[],
): string | null {
  if (coefficients.length !== speciesCount(entry)) return null;
  if (
    coefficients.some(
      (value) => !Number.isInteger(value) || value < MIN_COEFFICIENT || value > MAX_COEFFICIENT,
    )
  ) {
    return null;
  }
  const format = (species: EquationSpecies, coefficient: number) =>
    `${coefficient > 1 ? coefficient : ''}${species.formula}`;
  const left = entry.reactants.map((species, index) => format(species, coefficients[index]));
  const right = entry.products.map((species, index) =>
    format(species, coefficients[entry.reactants.length + index]),
  );
  return `${left.join(' + ')} -> ${right.join(' + ')}`;
}

export type EquationCheckStatus = 'invalid' | 'unbalanced' | 'not-simplest' | 'balanced';

export interface EquationCheckResult {
  status: EquationCheckStatus;
  atoms: AtomBalance[];
  message: string;
}

/** Checks learner coefficients with the shared balance helper. Fails closed on invalid input. */
export function checkProcessEquation(
  entry: ProcessEquationCase,
  coefficients: readonly number[],
): EquationCheckResult {
  const input = buildEquationInput(entry, coefficients);
  const parsed = input ? parseEquation(input) : null;
  if (!parsed?.equation) {
    return {
      status: 'invalid',
      atoms: [],
      message: `Each coefficient must be a whole number from ${MIN_COEFFICIENT} to ${MAX_COEFFICIENT}.`,
    };
  }
  const atoms = getAtomBalance(parsed.equation);
  if (!isEquationBalanced(parsed.equation)) {
    return { status: 'unbalanced', atoms, message: getBalanceHint(parsed.equation) };
  }
  if (!isSimplestCoefficientRatio(parsed.equation)) {
    return {
      status: 'not-simplest',
      atoms,
      message:
        'Every atom balances, but all the coefficients share a common factor. Divide them all by it to get the simplest whole-number ratio.',
    };
  }
  return {
    status: 'balanced',
    atoms,
    message: `Balanced. ${entry.note}`,
  };
}

export function startingCoefficients(entry: ProcessEquationCase): number[] {
  return Array.from({ length: speciesCount(entry) }, () => MIN_COEFFICIENT);
}
